import { useI18n } from '#imports';
import { ref } from 'vue';
import type {
  AutocompleteResultEntry,
  GoogleMapsAutocompleteApiResponse,
  GoogleMapsGeocodeApiResponse,
  LocationResult,
} from '@seb-platform/shared/types/google-maps';

interface LocationResultSuccess extends LocationResult {
  status: 'success';
}

interface LocationResultNotFound {
  status: 'not_found';
  message: string;
  location: null;
}

export type LocationResultResponse = LocationResultSuccess | LocationResultNotFound;

interface RequestPlacesAutocompleteSuccess {
  status: 'success';
  suggestions: AutocompleteResultEntry[];
}

interface RequestPlacesAutocompleteNotFound {
  status: 'not_found';
  message: string;
}

export type RequestPlacesAutocompleteResponse = RequestPlacesAutocompleteSuccess | RequestPlacesAutocompleteNotFound;

export function useGoogleMaps() {
  const isLoading = ref(false);
  const autocompleteError = ref('');
  const { t } = useI18n();

  async function getLocationByQuery(query: string): Promise<LocationResultResponse> {
    isLoading.value = true;
    const { status, data } = await $fetch<GoogleMapsGeocodeApiResponse>('/api/google-maps/geocode', {
      params: { query },
    });
    isLoading.value = false;
    if (status === 'success') {
      return {
        status,
        location: data.location,
        countryCode: data.countryCode,
      };
    }
    return { status, message: t('useGoogleMaps.locationNotFound'), location: null };
  }

  async function getLocationByPlaceId(placeId: string): Promise<LocationResultResponse> {
    isLoading.value = true;
    const { status, data } = await $fetch<GoogleMapsGeocodeApiResponse>('/api/google-maps/geocode', {
      params: { placeId },
    });
    isLoading.value = false;
    if (status === 'success') {
      return {
        status,
        location: data.location,
        countryCode: data.countryCode,
      };
    }
    return { status, message: t('useGoogleMaps.locationNotFound'), location: null };
  }

  async function requestPlacesAutocomplete(query: string): Promise<RequestPlacesAutocompleteResponse> {
    isLoading.value = true;
    const { data } = await $fetch<GoogleMapsAutocompleteApiResponse>('/api/google-maps/autocomplete', {
      params: { query: query },
    });
    isLoading.value = false;
    if (data) {
      return { status: 'success', suggestions: data.suggestions };
    }
    return {
      status: 'not_found',
      message: t('useGoogleMaps.locationNotFound'),
    };
  }

  return {
    getLocationByQuery,
    getLocationByPlaceId,
    requestPlacesAutocomplete,
    isLoading,
    autocompleteError,
  };
}
